<template>
  <div class="p-tablist-content" :data-testid="props.testId ?? `prefilter-tabs-${props.dataKey}`">
    <div
      class="p-tablist-tab-list"
      role="tablist"
      aria-orientation="horizontal"
      :data-testid="`${props.testId ?? `prefilter-tabs-${props.dataKey}`}-list`"
    >
      <div v-for="(tab, tabIndex) in tabs" :key="tab.title || tabIndex">
        <button
          :key="tab.title"
          class="p-tab"
          :class="{ 'p-tab-active': activeTabIndex === tabIndex }"
          type="button"
          role="tab"
          :aria-selected="activeTabIndex === tabIndex"
          :data-testid="`${props.testId ?? `prefilter-tabs-${props.dataKey}`}-tab-${tabIndex}`"
          :data-tab-index="tabIndex"
          :data-active="activeTabIndex === tabIndex ? 'true' : 'false'"
          :data-option-index="String(activeOptionIndexByTab[tabIndex] ?? 0)"
          :data-option-count="String(tab.options.length)"
          :data-option-title="tab.options[activeOptionIndexByTab[tabIndex] ?? 0]?.title ?? ''"
          @click="() => onTabClick(tabIndex, tab)"
        >
          <span
            :data-testid="`${props.testId ?? `prefilter-tabs-${props.dataKey}`}-label-${tabIndex}`"
          >
            {{ getTabLabel(tab, tabIndex) }}
            <span
              v-if="tab.options.length > 1"
              class="text-xs count ml-2"
              :data-testid="`${props.testId ?? `prefilter-tabs-${props.dataKey}`}-count-${tabIndex}`"
            >
              ({{ (activeOptionIndexByTab[tabIndex] ?? 0) + 1 }}/{{ tab.options.length }})
            </span>
          </span>

          <span
            v-if="activeTabIndex === tabIndex"
            class="p-tablist-active-bar-inside"
            role="presentation"
            :data-testid="`${props.testId ?? `prefilter-tabs-${props.dataKey}`}-activebar-${tabIndex}`"
            aria-hidden="true"
          />
        </button>
        <div v-if="showSelectForTab[tabIndex]" class="selectWrapper">
          <Select
            :modelValue="activeOptionIndexByTab[tabIndex]"
            :options="
              tab.options.map((opt, i) => ({
                label: translateI18nOrValue(i18n, opt.title),
                value: i,
              }))
            "
            optionLabel="label"
            optionValue="value"
            appendTo="body"
            class="prefilter-select"
            autoFocus
            @change="
              (event) => {
                activeOptionIndexByTab[tabIndex] = event.value;
                onDropdownChange(tabIndex);
              }
            "
            @on-hide="() => (showSelectForTab[tabIndex] = false)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useTablePrefilterStore } from '@/stores/tablePrefilter';
import { translateOrValue as translateI18nOrValue } from '@/utils/i18n';
import type { PrefilterOption } from '@/utils/prefilter';
const i18n = useI18n();

const prefilterStore = useTablePrefilterStore();
const MAX_TABS = 5;
const isDropdownOpen = ref<boolean[]>([]);
const showSelectForTab = ref<{ [key: number]: boolean }>({});

export interface PrefilterTab {
  title: string;
  options: PrefilterOption[];
  initialOptionIndex?: number;
}

interface DataTableFilterProps {
  tabs: PrefilterTab[];
  focusedColumns: string[];
  dataKey: string;
  testId?: string;
}

const props = defineProps<DataTableFilterProps>();
const emit = defineEmits<{
  (e: 'change', payload: PrefilterOption): void;
}>();

const activeTabIndex = ref(0);
const activeOptionIndexByTab = ref<number[]>([]);
const activeBarStyle = ref('');

function onDropdownChange(tabIndex: number) {
  activeTabIndex.value = tabIndex;

  // save tab + dropdown option state
  prefilterStore.setSelectedTab(props.dataKey, activeTabIndex.value);
  prefilterStore.setOptionIndices(props.dataKey, activeOptionIndexByTab.value);

  emitActiveOption();
  showSelectForTab.value[tabIndex] = false;
}

function closeAllSelects() {
  showSelectForTab.value = Object.fromEntries(Object.keys(props.tabs).map((_, i) => [i, false]));
}

function initializeActiveStates() {
  if (!props.tabs || props.tabs.length === 0) return;

  const savedTabIndex = prefilterStore.getSelectedTab(props.dataKey);
  const savedOptionIndices = prefilterStore.getOptionIndices(props.dataKey);

  isDropdownOpen.value = props.tabs.map(() => false);

  // prepare empty index array
  activeOptionIndexByTab.value = props.tabs.map((tab, i) => {
    const savedOptionIndex = savedOptionIndices[i];
    if (savedOptionIndex !== undefined && savedOptionIndex < tab.options.length) {
      return savedOptionIndex;
    }

    const initialFromConfig = tab.options.findIndex((opt) => opt.initialActive);
    return initialFromConfig >= 0 ? initialFromConfig : (tab.initialOptionIndex ?? 0);
  });

  // determine initial tab
  if (savedTabIndex !== undefined && savedTabIndex >= 0 && savedTabIndex < props.tabs.length) {
    activeTabIndex.value = savedTabIndex;
  } else {
    const initialTab = props.tabs.findIndex((tab, i) => {
      const idx = activeOptionIndexByTab.value[i] ?? 0;
      const opt = tab.options[idx];
      return opt?.initialActive;
    });
    activeTabIndex.value = initialTab >= 0 ? initialTab : 0;
  }

  // 🔁 Delayed emit to ensure rendering is ready
  void nextTick(() => {
    emitActiveOption();
    updateActiveBar();
  });
}

function getTabLabel(tab: PrefilterTab, tabIndex: number): string {
  const optionIndex = activeOptionIndexByTab.value[tabIndex] ?? 0;
  const activeOption = tab.options[optionIndex];

  const tabTitle = translateI18nOrValue(i18n, tab.title);
  const optionTitle = translateI18nOrValue(i18n, activeOption?.title);

  return tab.options.length > 1 ? `${tabTitle}: ${optionTitle}` : tabTitle;
}

function onTabClick(tabIndex: number, tab?: PrefilterTab) {
  closeAllSelects();
  if (activeTabIndex.value === tabIndex) {
    const currentTab = tab ?? props.tabs[tabIndex];
    if (!currentTab) return;

    if (currentTab.options.length > MAX_TABS) {
      showSelectForTab.value = { ...showSelectForTab.value, [tabIndex]: true };
      return;
    }

    // when active → next option
    const currentIndex = activeOptionIndexByTab.value[tabIndex] ?? 0;
    const optionsLength = currentTab.options.length || 1;
    const nextIndex = (currentIndex + 1) % optionsLength;
    activeOptionIndexByTab.value[tabIndex] = nextIndex;
  }

  // activate tab
  activeTabIndex.value = tabIndex;

  // save tab state to store
  prefilterStore.setSelectedTab(props.dataKey, activeTabIndex.value);
  prefilterStore.setOptionIndices(props.dataKey, activeOptionIndexByTab.value);

  emitActiveOption();
}

function emitActiveOption() {
  const tab = props.tabs[activeTabIndex.value];
  if (!tab) return;

  const optIndex = activeOptionIndexByTab.value[activeTabIndex.value] ?? 0;
  const option = tab.options[optIndex];

  if (!option) return;

  emit('change', option);
}

function updateActiveBar() {
  void nextTick(() => {
    const tabList = document.querySelector('.p-tablist-tab-list');
    const activeTab = tabList?.querySelector('.p-tab-active') as HTMLElement | null;
    if (activeTab && tabList) {
      const offsetLeft = activeTab.offsetLeft;
      const width = activeTab.offsetWidth;
      activeBarStyle.value = `width: ${width}px; left: ${offsetLeft}px;`;
    }
  });
}

onMounted(() => {
  initializeActiveStates();
});

watch(() => props.tabs, initializeActiveStates, { immediate: true });
watch(activeTabIndex, updateActiveBar);
</script>

<style scoped lang="css">
@layer app {
  .p-tablist-tab-list {
    display: flex;
    border-bottom: none;
    position: relative;
  }

  .p-tab {
    position: relative;
    padding-top: 0.6rem;
    padding-left: 1rem;
    padding-right: 1rem;
    padding-bottom: 0.6rem;
    font-weight: 600;
    border: none;
    background: none;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .p-tablist-active-bar-inside {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 4px;
    background-color: theme('colors.interactionblue.500');
    border-radius: 0;
    margin: 0;
    padding: 0;
  }

  .p-tab:hover {
    color: theme('colors.interactionblue.500');
  }

  .count {
    color: var(--text-secondary);
  }

  .p-tablist-active-bar {
    position: absolute;
    height: 5px;
    background-color: theme('colors.interactionblue.500');
    bottom: -1px;
    transition:
      left 0.25s ease,
      width 0.25s ease;
  }

  .selectWrapper {
    position: absolute;
    overflow: visible;
    z-index: 999;
    bottom: 0;
    height: auto;
  }

  .prefilter-select {
    position: relative;
    width: 220px;
  }
}
</style>
